import Octicon, { X } from '@githubprimer/octicons-react';
import classnames from 'classnames';
import PopoverTitle from 'components/shared/PopoverTitle';
import CheckCircleSVG from 'components/shared/svg/CheckCircleSVG';
import MagnifyingGlassSVG from 'components/shared/svg/MagnifyingGlassSVG';
import RightChevronSVG from 'components/shared/svg/RightChevronSVG';
import * as React from 'react';
import { Button, Input, InputGroup, ListGroup, ListGroupItem } from 'reactstrap';
import { Filter, SelectedFilter } from '.';
import { FilterChangeLocation } from '../ListingContainer';
import { toKebabCase } from 'util/StringUtil';

interface AvailableFiltersProps {
	filters: Filter[];
	selectedFilters: SelectedFilter[];
	beginFilterChange: (filter: SelectedFilter, location: FilterChangeLocation) => void;
	handlePopoverClose: () => void;
}

interface AvailableFiltersState {
	filterKeyword: string;
}

// comparison function to alphabetically sort the available filters
const compare = (a: Filter, b: Filter) =>
	a.displayName.toLocaleLowerCase().localeCompare(b.displayName.toLocaleLowerCase());

/**
 * Component that displays a list of available filters for users to add.
 */
class AvailableFilters extends React.Component<AvailableFiltersProps, AvailableFiltersState> {
	private searchInput;

	constructor(props) {
		super(props);
		this.searchInput = React.createRef();

		this.state = {
			filterKeyword: '',
		};
	}

	/**
	 * Helps filter out search filters
	 *
	 * @memberof AvailableFilters
	 */
	public handleKeywordSearch = (e: React.FormEvent<HTMLInputElement>) => {
		const value = e.currentTarget.value;

		this.setState({
			filterKeyword: value,
		});
	};

	/**
	 * Clear filter search state.
	 *
	 * @private
	 * @memberof AvailableFilters
	 */
	private clearFilterSearch = () => {
		this.setState({
			filterKeyword: '',
		});
		this.focusSearch();
	};

	/**
	 * Set focus on the filter search input.
	 *
	 * @private
	 * @memberof AvailableFilters
	 */
	private focusSearch = () => {
		this.searchInput.current.focus();
	};

	/**
	 * Depending on the filter that is being selected, if the filter already exists, it will grab it from the list
	 * of selected filters, so that the value is included, otherwise, it'll pass the empty filter so that values
	 * can be set.
	 *
	 * @private
	 * @memberof AvailableFilters
	 */
	private handleFilterSelection = (filter: Filter) => {
		const match = this.props.selectedFilters.find((indexFilter) => indexFilter.name === filter.name);

		if (!match) {
			this.props.beginFilterChange(filter as SelectedFilter, FilterChangeLocation.FilterList);
		} else {
			this.props.beginFilterChange(match, FilterChangeLocation.FilterList);
		}
	};

	public render() {
		const availableFilters = this.props.filters.sort(compare);

		if (availableFilters.length === 0) {
			return <em>No more search filters available.</em>;
		}

		const filteredFilters = availableFilters.filter(
			(filter) =>
				!filter.implicit &&
				(filter.displayName.toLocaleLowerCase().includes(this.state.filterKeyword.toLocaleLowerCase()) ||
					filter.name.toLocaleLowerCase().includes(this.state.filterKeyword.toLocaleLowerCase())),
		);

		const ignoreFilters = this.props.selectedFilters.map((filter) => filter.name);

		return (
			<>
				<PopoverTitle titleText="Filter" close={this.props.handlePopoverClose} />

				<div className="filter-list__search-wrapper">
					<InputGroup className="input-group-nowrap">
						<Input
							type="search"
							id="filterSearch"
							placeholder="Enter Keyword"
							className="filter-list__search"
							onChange={this.handleKeywordSearch}
							onKeyDown={(e: React.KeyboardEvent) => {
								if (e.which === 13) {
									e.preventDefault();

									if (filteredFilters.length === 1) {
										this.handleFilterSelection(filteredFilters[0]);
									}
								}
							}}
							value={this.state.filterKeyword}
							autoFocus
							innerRef={this.searchInput}
							data-testid="search-filters-list"
						/>

						{this.state.filterKeyword.length > 0 ? (
							<Button
								type="button"
								color="default"
								className="clear-text-btn"
								onClick={() => {
									this.clearFilterSearch();
								}}
								title="Remove"
							>
								<Octicon icon={X} ariaLabel="X" />
							</Button>
						) : null}
					</InputGroup>
					<MagnifyingGlassSVG class="filter-list__search-icon" />
				</div>
				<ListGroup flush className="filter-list">
					{filteredFilters.map((filter, index) => {
						const filterIsIgnored = ignoreFilters.includes(filter.name);

						return (
							<ListGroupItem
								key={index}
								tag="button"
								className={classnames('filter-list__item', {
									active: filterIsIgnored,
								})}
								action
								onClick={(e) => {
									e.preventDefault();
									this.handleFilterSelection(filter);
								}}
								data-testid={`filter-list-item-${toKebabCase(filter.displayName)}`}
							>
								<span className="filter-list__text">
									{filter.displayName}
									{filterIsIgnored ? <CheckCircleSVG class="filter-list__item-icon" /> : null}
								</span>

								<RightChevronSVG class="filter-list__chevron" />
							</ListGroupItem>
						);
					})}
					{filteredFilters.length === 0 ? <em>No matches found.</em> : null}
				</ListGroup>
			</>
		);
	}
}

export default AvailableFilters;
